import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "audio",
    "playButton",
    "playIcon",
    "pauseIcon",
    "progressContainer",
    "currentTime",
    "duration",
    "speedButton",
    "speedIndicator",
    "bar",
    "scrubber"
  ]

  static values = {
    duration: Number
  }

  connect() {
    this.isPlaying = false
    this.currentSpeed = 1
    this.speeds = [1, 1.5, 2, 0.5]
    this.speedIndex = 0
    this.isDragging = false

    // Set up audio event listeners
    this.audioTarget.addEventListener("loadedmetadata", () => this.updateDuration())
    this.audioTarget.addEventListener("timeupdate", () => this.updateProgress())
    this.audioTarget.addEventListener("ended", () => this.handleEnded())

    // Set up drag event listeners
    this.handleDragBound = (e) => this.handleDrag(e)
    this.stopDragBound = () => this.stopDrag()
    document.addEventListener("mousemove", this.handleDragBound)
    document.addEventListener("mouseup", this.stopDragBound)

    // Initialize duration if available from metadata
    if (this.durationValue > 0) {
      this.durationTarget.textContent = this.formatTime(this.durationValue)
    }
  }

  disconnect() {
    if (this.handleDragBound) {
      document.removeEventListener("mousemove", this.handleDragBound)
    }
    if (this.stopDragBound) {
      document.removeEventListener("mouseup", this.stopDragBound)
    }
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
  }

  play() {
    // Pause any other playing audio on the page
    document.querySelectorAll("audio").forEach(audio => {
      if (audio !== this.audioTarget && !audio.paused) {
        audio.pause()
        // Reset other players' UI
        const controller = audio.closest("[data-controller='audio-player']")
        if (controller) {
          const playIcon = controller.querySelector("[data-audio-player-target='playIcon']")
          const pauseIcon = controller.querySelector("[data-audio-player-target='pauseIcon']")
          if (playIcon) playIcon.classList.remove("hidden")
          if (pauseIcon) pauseIcon.classList.add("hidden")
        }
      }
    })

    this.audioTarget.play()
    this.isPlaying = true
    this.playIconTarget.classList.add("hidden")
    this.pauseIconTarget.classList.remove("hidden")
  }

  pause() {
    this.audioTarget.pause()
    this.isPlaying = false
    this.playIconTarget.classList.remove("hidden")
    this.pauseIconTarget.classList.add("hidden")
  }

  handleEnded() {
    this.isPlaying = false
    this.playIconTarget.classList.remove("hidden")
    this.pauseIconTarget.classList.add("hidden")
    this.updateBarColors(0)
    this.currentTimeTarget.textContent = "00:00"
    this.updateScrubberPosition(0)
  }

  startDrag(event) {
    if (event.button !== 0) return // Only left mouse button
    this.isDragging = true
    this.progressContainerTarget.classList.add("cursor-grabbing")
    this.seek(event)
  }

  handleDrag(event) {
    if (!this.isDragging) return
    this.seek(event)
  }

  stopDrag() {
    this.isDragging = false
    this.progressContainerTarget.classList.remove("cursor-grabbing")
  }

  seek(event) {
    const rect = this.progressContainerTarget.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100))
    
    if (this.audioTarget.duration) {
      this.audioTarget.currentTime = (percentage / 100) * this.audioTarget.duration
      this.updateScrubberPosition(percentage)
      this.updateBarColors(percentage)
      this.currentTimeTarget.textContent = this.formatTime(this.audioTarget.currentTime)
    }
  }

  updateScrubberPosition(percentage) {
    if (this.hasScrubberTarget) {
      this.scrubberTarget.style.left = `${percentage}%`
      // Show scrubber when dragging or hovering
        if (this.isDragging) {
            this.scrubberTarget.classList.add("opacity-100")
            this.scrubberTarget.classList.remove("opacity-0")
        }
    }
  }

  updateProgress() {
    if (this.audioTarget.duration && !this.isDragging) {
      const percentage = (this.audioTarget.currentTime / this.audioTarget.duration) * 100
      this.currentTimeTarget.textContent = this.formatTime(this.audioTarget.currentTime)
      this.updateScrubberPosition(percentage)
      this.updateBarColors(percentage)
    }
  }

  updateBarColors(percentage) {
    if (!this.hasBarTarget) return
    const totalBars = this.barTargets.length
    const progressedBars = Math.floor((percentage / 100) * totalBars)
    this.barTargets.forEach((bar, index) => {
      if (index < progressedBars) {
        bar.classList.remove("bg-white/30")
        bar.classList.add("bg-white")
      } else {
        bar.classList.add("bg-white/30")
        bar.classList.remove("bg-white")
      }
    })
  }

  updateDuration() {
    if (this.audioTarget.duration) {
      const formatted = this.formatTime(this.audioTarget.duration)
      this.durationTarget.textContent = formatted
    }
  }

  toggleSpeed() {
    this.speedIndex = (this.speedIndex + 1) % this.speeds.length
    this.currentSpeed = this.speeds[this.speedIndex]
    this.audioTarget.playbackRate = this.currentSpeed
    const speedText = `${this.currentSpeed}x`
    this.speedButtonTarget.textContent = speedText
    if (this.hasSpeedIndicatorTarget) {
      this.speedIndicatorTarget.textContent = speedText
    }
  }

  formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return "00:00"
    const totalSeconds = Math.floor(seconds)
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    // Always format as MM:SS (e.g., 00:09 for 9 seconds)
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }
}

